import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { DEFAULT_THING_ID, DEFAULT_THING_VERSION } from '@kemu-io/kemu-core/common/constants';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Alert, Layout, Spin } from 'antd';
import classNames from 'classnames';
import { LoadingOutlined } from '@ant-design/icons';
import { selectCurrentRecipePoolId, selectOpeningRecipe } from '../Workspace/workspaceSlice';
import { downloadRecipeAction } from '../interface/reducers/downloadRecipeReducer';
import useAlert from '../../components/alert/useAlert';
// import LoadingEnvironmentScreen from '../LoadingEnvironmentScreen/LoadingEnvironmentScreen';
import styles from './BrowserRecipeScreen.module.css';
import LogicMapperSection from '@components/layout/HorizontalSplit/LogicMapperSection';
import { SelectedBlockInfo } from '@src/types/core_t';
import MainMenu from '@components/mainMenu/mainMenu';
import useNavigateWithSearch from '@common/hooks/useNavigateWithSearch';
import { createBrowserRecipeAction } from '@src/app/reducers/workspace/createBrowserRecipeReducer';
import useTranslation from '@hooks/useTranslation';
import useLoadRecipeFromUrl from '@common/hooks/useLoadRecipeFromUrl';
import FormGroup from '@components/form-control/formGroup/formGroup';
import routes from '@common/routes';
import RoundedModal from '@components/roundedModal/roundedModal';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import { SITE_LAYOUT_ELEMENT_CLASS } from '@common/constants';
import { ShowHelpConfig } from '@common/context/LogicMapperContext/LogicMapperContext';
import { selectColorScheme } from '@src/features/interface/interfaceSlice';


const isKemuDomain = (url: string) => {
  const urlObj = new URL(url);
  const matches = urlObj.hostname.endsWith('.kemu.io');
  return matches;
};

/**
 * Invoked when the path contains a browser recipe type.
 * It downloads a recipe if it exists in the path, otherwise it creates a default recipe and opens it.
 */
const BrowserRecipeScreen = (): React.JSX.Element | null => {
  const dispatch = useDispatch();
  const t = useTranslation('BrowserRecipeScreen');
  const alert = useAlert();
  const navigateWithSearch = useNavigateWithSearch();
  const [queryParams, setQueryParams] = useSearchParams();
  const [downloadingRecipe, setDownloadingRecipe] = useState(false);
  const publicRecipeUrl = queryParams.get('recipe_url');
  const loadRecipeFromUrl = useLoadRecipeFromUrl();
  const { id: recipeIdInPath } = useParams<{id?: string}>();
  const recipePoolId = useSelector(selectCurrentRecipePoolId);
  const downloadRecipeRef = useRef(false);
  const baseRecipeCreatedRef = useRef(false);
  const openingRecipe = useSelector(selectOpeningRecipe);
  const [conditionsCheckbox, setConditionsCheckbox] = useState(false);
  const targetRecipeUrl = useRef<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { profile: userProfile } = useSelector(selectSignedUserProfile);
  const [showHelpConfig, setShowHelpConfig] = useState<ShowHelpConfig | null>(null);
  const colorScheme = useSelector(selectColorScheme);
  
  const defaultThing: SelectedBlockInfo = useMemo(() => {
    return {
      DbId: DEFAULT_THING_ID,
      recipeId: DEFAULT_THING_ID,
      recipePoolId: recipePoolId || '',
      title: '',
      version: DEFAULT_THING_VERSION,
    };
  }, [recipePoolId]);

  const handleShowHelp = useCallback((config: ShowHelpConfig | null) => {
    setShowHelpConfig(config);
  }, []);

  /**
   * Creates and loads a default recipe.
   */
  const loadBaseRecipe = useCallback(async () => {
    if (!baseRecipeCreatedRef.current && userProfile) {
      baseRecipeCreatedRef.current = true;  // prevents creating the default recipe more than once
      dispatch(createBrowserRecipeAction({
        navigate: navigateWithSearch,
        user: {
          id: userProfile.id,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
        }
      }));
    }
  }, [navigateWithSearch, dispatch, userProfile]);

  const cleanup = useCallback(() => {
    setDownloadingRecipe(false);
    setShowConfirmation(false);
    setConditionsCheckbox(false);
    targetRecipeUrl.current = null;
    setQueryParams((params) => {
      params.delete('recipe_url');
      return params;
    });
  }, [setQueryParams]);


  const handleDoOpen = useCallback(async () => {
    if (!targetRecipeUrl.current) { return; }

    setDownloadingRecipe(true);
    // await new Promise((resolve) => setTimeout(resolve, 2500));
    await loadRecipeFromUrl(targetRecipeUrl.current, {
      onError: () => {
        cleanup();
      }
    });

    cleanup();
  }, [setDownloadingRecipe, loadRecipeFromUrl, cleanup]);


  const abortImport = useCallback(() => {
    cleanup();
  }, [cleanup]);


  const fetchRecipeData = useCallback(async (url: string) => {
    if (targetRecipeUrl.current) { return; }

    targetRecipeUrl.current = url;
    // Recipes from kemu.io are trusted and can be opened immediately
    setShowConfirmation(true);

    if (isKemuDomain(url)) {
      return handleDoOpen();
    }

    setConditionsCheckbox(false);
  }, [handleDoOpen]);



  useEffect(() => {
    if (showConfirmation || downloadingRecipe || !userProfile) { return; }
    // If there is a target recipe to be downloaded, do that.
    if (publicRecipeUrl ) {
      fetchRecipeData(publicRecipeUrl);
      return;
    }

    // If the recipe id is in the path, download the recipe
    if (!recipePoolId && recipeIdInPath) {
      if (!downloadRecipeRef.current) { // Prevent downloading the recipe more than once
        downloadRecipeRef.current = true;
        // Download contents and initialized the recipe
        dispatch(downloadRecipeAction({
          recipeId: recipeIdInPath,
          alert,
          navigate: navigateWithSearch
        }));
      }
    } else if (!recipePoolId && !recipeIdInPath) {
      // If the recipe id is not in the path, create a default recipe
      loadBaseRecipe();
    }
  }, [
    recipeIdInPath, alert, navigateWithSearch, dispatch, userProfile,
    recipePoolId, fetchRecipeData, publicRecipeUrl, loadBaseRecipe, showConfirmation, downloadingRecipe
  ]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div id="layout-container" data-theme={colorScheme}>
        <Layout className={classNames('layout-section', styles.Layout, SITE_LAYOUT_ELEMENT_CLASS)} >
          <RoundedModal
            visible={showConfirmation}
            title={t('OpenKemuFile.Title')}
            onCancel={abortImport}
            closeOnMaskClick={false}
            closable={false}
            onOk={handleDoOpen}
            disableOkButton={!conditionsCheckbox || downloadingRecipe}
            disableCancelButton={downloadingRecipe}
            okBtnLabel={t('OpenKemuFile.OpenBtn')}
            cancelBtnLabel={t('OpenKemuFile.CancelBtn')}
          >
            {!downloadingRecipe ? (
              <>
                <Alert closable={false} type="warning" message={
                  <span>
                    <strong>{t.withBaseKey('CommonWords')('Warning').toLocaleUpperCase()}: </strong>
                    {t('OpenKemuFile.Warning')}
                  </span>
                }/>
              <FormGroup className={styles.AgreeContainer}>
                <input type="checkbox" name="agree" id="agree" onChange={(e) => setConditionsCheckbox(e.target.checked)} />
                <label className={styles.AgreeLabel} htmlFor="agree">
                  {t('OpenKemuFile.TermsText', undefined, {
                    risks: <a href={routes.recipe.getImportRecipeRisks()}>{t('OpenKemuFile.RisksWord')}</a>,
                  })}
                </label>
              </FormGroup>
              </>
            ) : (
              <div className={styles.LoadingContainer}>
                {t('DownloadingRecipe.Title')}
                <Spin spinning={true} indicator={<LoadingOutlined />} />
              </div>
            )}
          </RoundedModal>
          {/* {showConfirmation && (
          )} */}


          {!openingRecipe && !showConfirmation && recipePoolId && (
            <>
              <MainMenu collapsed={true} onShowHelp={handleShowHelp} />
              <LogicMapperSection
                openedBlock={defaultThing}
                recipePoolId={recipePoolId}
                visible={true}
                showMenuHelpConfig={showHelpConfig}
                // IMPORTANT: force react to render a new instance of the component when the recipe pool id changes, this in
                // order to auto-remove connections and ports via jsplumb
                key={recipePoolId}
              />
            </>
          )}
        </Layout>
      </div>
    </DndProvider>
  );
};

export default BrowserRecipeScreen;
